import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormHelperText,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import "./orderInvoice.scss";
import {
  InvoiceInputTableProps,
  InvoiceVerificationFormProps,
} from "../../shared/models/invoice";
import { getUserContext } from "../../shared/utils/utils";
import {
  cleanupResizeObserver,
  handleResizeObserverError,
} from "../../shared/utils/resizeObserverUtils";
import { getAllProductTypesApi } from "../../store/api/rfq";
import store from "../../store/store";
import { getPartyProductType } from "../../store/api/party";
import {
  DECIMAL_BOARD,
  ITEM_STATUS,
  ORDER_INPUT_TABLE_ROW,
  ORDER_WIZARD_ITEMS,
} from "../../shared/constants/constants";
import { createExternalInvoice } from "../../store/api/invoice";
import { ReactComponent as DeleteCustom } from "../../shared/assets/deleteIcon.svg";
import { AddBoxOutlined, Check } from "@mui/icons-material";
import AdjustmentDialog from "../dialogBox/adjustmentsDialog";
import {
  generateInvoiceFromAccounts,
  updateOrderStatus,
} from "../../store/api/order";
import BigNumber from "bignumber.js";
import { validateInvoiceUpload } from "../../shared/utils/validator";
import OrderItemListDialog from "./orderItemListDialog";
import { createOrderContent } from "../../store/api/content";

type PopupMessageTypes = {
  open: boolean;
  message: string;
};

interface CombinedProps
  extends InvoiceVerificationFormProps,
    InvoiceInputTableProps {
  onInvoiceCreated?: (value: boolean) => void;
  orderId: string;
  setValidationErrors: (errors: Record<string, string>) => void;
  orderItems: any;
  contentDetails: any;
  setSuccess: React.Dispatch<
    React.SetStateAction<{ open: boolean; message: string }>
  >;
}

const OrderInvoiceVerificationForm: React.FC<CombinedProps> = ({
  invoiceData,
  onDataChange,
  invoiceHeaderData,
  setInvoiceHeadData,
  handleAddInvoiceRow,
  ITEM_UOMs,
  validationErrors,
  resetValidationErrors,
  setError,
  products,
  adjustments,
  setAdjustments,
  onInvoiceCreated,
  orderId,
  setValidationErrors,
  orderItems,
  contentDetails,
  setSuccess,
}) => {
  // Safety check for invoiceData
  const safeInvoiceData = invoiceData || {};
  const dispatch = useDispatch();

  // Loading state for API call
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  // Table state management (original invoiceInputTable functionality)
  const [productType, setProductType] = useState<any>([]);
  const [partyProductType, setPartyProductType] = useState<any>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [activeItemNameIndex, setActiveItemNameIndex] = useState<null | number>(
    null
  );
  const [openDiscounts, setOpenDiscounts] = useState<boolean>(false);
  const [openItemList, setOpenItemList] = useState<boolean>(false);
  const [adjustmentValues, setAdjustmentValues] = useState({
    totalAdjustedAmount: 0,
    totalAdjustmentTax: 0,
    grandAdjustedAmount: 0,
  });
  const userContext: any = getUserContext();
  const orgPartyId = userContext?.orgPartyIds[0];
  const tableRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // Format the amount as Indian Rupee currency
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleFieldChange = (field: string, value: any) => {
    onDataChange(field, value);
  };

  const formatDateForInput = (dateValue: any): string => {
    if (!dateValue) return "";

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  };

  const getBillingAddress = () => {
    return safeInvoiceData.invoiceContactMechs?.find(
      (contact: any) => contact.purposeType === "BILLING_ADDRESS"
    );
  };

  const getShippingAddress = () => {
    return safeInvoiceData.invoiceContactMechs?.find(
      (contact: any) => contact.purposeType === "SHIPPING_ADDRESS"
    );
  };

  const billingAddress = getBillingAddress();
  const shippingAddress = getShippingAddress();

  // Handle ResizeObserver errors
  useEffect(() => {
    const cleanup = handleResizeObserverError();
    return () => {
      cleanup();
      cleanupResizeObserver(tableRef.current);
    };
  }, []);

  // Load product types and party product types
  useEffect(() => {
    const dispatchers = async () => {
      await store
        .dispatch(getAllProductTypesApi())
        .unwrap()
        .then((res: any) => {
          setProductType(res.data);
        });

      await store
        .dispatch(getPartyProductType({ partyIds: [orgPartyId] }))
        .unwrap()
        .then((res) => {
          res.data.forEach((element: any) => {
            if (element.partyId === orgPartyId) {
              setPartyProductType([...element?.productTypeIds]);
            }
          });
        });
    };
    dispatchers();
  }, []);

  // Calculate adjustments
  useEffect(() => {
    let totalAdjustment = 0;
    let totalAdjustmentTax = 0;
    let grandAdjustedAmount = 0;
    adjustments?.forEach((adjustment: any) => {
      grandAdjustedAmount += Number(adjustment?.adjustmentTotal ?? 0);
      totalAdjustmentTax += Number(adjustment?.adjustmentTax);
      totalAdjustment += Number(adjustment?.adjustmentAmount ?? 0);
    });
    setAdjustmentValues({
      grandAdjustedAmount: grandAdjustedAmount,
      totalAdjustmentTax: totalAdjustmentTax,
      totalAdjustedAmount: totalAdjustment,
    });
  }, [adjustments]);

  // Table handlers
  const handleItemNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = event.target.value;
    handleInputChange(index, "productId", "", `productId_${index}`);

    if (value) {
      const filtered = products.filter((product: any) =>
        product.productName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }

    handleInputChange(index, "item", value, `itemName_${index}`);
  };

  const handleSelectItem = (product: any, index: any) => {
    handleInputChange(index, "item", product?.productName, `item_${index}`);
    handleInputChange(index, "productId", product?._id, `productId_${index}`);
    handleInputChange(index, "hsnCode", product?.hsnCode, `hsnCode_${index}`);
    handleInputChange(
      index,
      "brandName",
      product?.brandName,
      `brandName_${index}`
    );
    handleInputChange(
      index,
      "itemTypeId",
      product?.productTypeId,
      `itemTypeId_${index}`
    );
    handleInputChange(index, "gst", product?.gst, `gst_${index}`);
    setActiveItemNameIndex(null);
  };

  const handleInputChange = (
    index: number,
    name: string,
    value: any,
    errorName: string
  ) => {
    resetValidationErrors("items_div");
    resetValidationErrors(errorName);

    (setInvoiceHeadData as any)((prevData: any) => {
      const updatedItems = [...prevData.items];
      const currentItem = updatedItems[index];
      let totalAmount = parseFloat(
        Number(prevData.totalAmount ?? 0).toFixed(2)
      );
      let totalTaxAmount = parseFloat(
        Number(prevData.totalTaxAmount ?? 0).toFixed(2)
      );
      let grandTotal = parseFloat(Number(prevData.grandTotal ?? 0).toFixed(2));

      const removeOldItemValues = () => {
        const itemAmountNum = Number(currentItem.amount ?? 0);
        const itemTaxableAmountNum =
          (currentItem.pricePerUnit ?? 0) * (Number(currentItem.quantity) ?? 0);
        const itemTaxAmountNum =
          (currentItem.pricePerUnit ?? 0) *
          (Number(currentItem.quantity) ?? 0) *
          (currentItem.gst ?? 0) *
          0.01;
        grandTotal -= parseFloat(itemAmountNum.toFixed(2));
        totalTaxAmount -= parseFloat(itemTaxAmountNum.toFixed(2));
        totalAmount -= parseFloat(itemTaxableAmountNum.toFixed(2));
      };

      const recalculateAndUpdateItem = (updatedField: any) => {
        const priceSplit = updatedField?.pricePerUnit?.toString()?.split(".");
        let finalPrice = updatedField?.pricePerUnit || "0";
        if (priceSplit?.length > 1) {
          if (priceSplit[1]?.length > 0) {
            finalPrice = priceSplit?.join(".");
          } else {
            finalPrice = priceSplit[0];
          }
        }
        const price = Number(finalPrice ?? 0);
        const qty = Number(updatedField?.quantity ?? 0);
        const gst = Number(updatedField?.gst ?? 0);

        let amount = price * qty;
        // if (gst > 0) {
        //     amount += amount * gst * 0.01;
        // }
        amount = parseFloat(
          (Math.round((amount + Number.EPSILON) * 100) / 100).toFixed(2)
        );

        const taxAmount = (price ?? 0) * qty * (gst ?? 0) * 0.01;

        totalAmount += amount;
        totalTaxAmount += parseFloat(taxAmount.toFixed(2));
        grandTotal = parseFloat((totalAmount + totalTaxAmount).toFixed(2));

        // console.log("Calculations :: ", {
        //     finalPrice: finalPrice,
        //     price: price,
        //     quantity: qty,
        //     gst: gst,
        //     amount: amount,
        //     taxAmount: taxAmount,
        //     totalAmount: totalAmount,
        //     totalTaxAmount: totalTaxAmount,
        //     grandTotal: grandTotal
        // })

        updatedField.amount = Number((amount + taxAmount).toFixed(2));
        updatedField.finalUnitPrice = price;
        return updatedField;
      };

      if (name === "gst" || name === "pricePerUnit" || name === "quantity") {
        removeOldItemValues();

        let decimals =
          DECIMAL_BOARD[prevData?.items?.[index]?.["quantityUOM"] ?? "REST"];
        let num = value?.toString()?.split(".") || ["0"];
        if (!decimals) {
          decimals = 2;
        }
        // console.log("Number :: ", prevData?.items?.[index]?.[name]?.toString()?.split("."));
        if (
          num?.length > 2 ||
          num[0].length > 6 ||
          num[1]?.length > decimals ||
          (num[1]?.length > 0 && isNaN(num[1])) ||
          (num[0]?.length > 0 && isNaN(num[0]))
        ) {
          num = prevData?.items?.[index]?.[name]?.toString()?.split(".");
        }

        let newValue = num?.join(".");
        // if (isNaN(newValue) || value === '' || value === null) {
        //     newValue = 0;
        // }
        // console.log("New Value :: ", newValue);
        const updatedField = {
          ...currentItem,
          [name]: newValue,
        };

        updatedItems[index] = recalculateAndUpdateItem(updatedField);
      } else {
        updatedItems[index] = {
          ...currentItem,
          [name]: value,
        };
      }

      totalAmount = parseFloat(
        (Math.round((totalAmount + Number.EPSILON) * 100) / 100).toFixed(2)
      );
      totalTaxAmount = parseFloat(
        (Math.round((totalTaxAmount + Number.EPSILON) * 100) / 100).toFixed(2)
      );
      grandTotal = parseFloat(
        (Math.round((grandTotal + Number.EPSILON) * 100) / 100).toFixed(2)
      );

      return {
        ...prevData,
        items: updatedItems,
        totalAmount: totalAmount,
        grandTotal: grandTotal,
        totalTaxAmount: totalTaxAmount,
      };
    });
  };

  const handleDeleteItemRow = (index: number) => {
    (setInvoiceHeadData as any)((prevData: any) => {
      const updatedItems = [...(prevData.items || [])];
      updatedItems.splice(index, 1);

      let totalAmount = 0;
      let totalTaxAmount = 0;
      let grandTotal = 0;

      updatedItems.forEach((item: any) => {
        const quantity = parseFloat(item.quantity ?? 0);
        const unitPrice = parseFloat(item.pricePerUnit ?? 0);
        const gst = parseFloat(item.gst ?? 0);

        const itemBaseAmount = unitPrice * quantity;
        const itemTaxAmount = itemBaseAmount * gst * 0.01;
        const itemFinalAmount = itemBaseAmount + itemTaxAmount;

        totalAmount += itemBaseAmount;
        totalTaxAmount += itemTaxAmount;
        grandTotal += itemFinalAmount;
      });

      totalAmount = parseFloat(totalAmount.toFixed(2));
      totalTaxAmount = parseFloat(totalTaxAmount.toFixed(2));
      grandTotal = parseFloat(grandTotal.toFixed(2));

      return {
        ...prevData,
        items: updatedItems,
        totalAmount,
        totalTaxAmount,
        grandTotal,
      };
    });
  };

  const addAdjustments = (newAdjustments: any) => {
    setAdjustments([...newAdjustments]);
    setOpenDiscounts(false);
  };

  useEffect(() => {
    console.log(validationErrors);
  }, []);

  const validateInputs = (invoice: any): boolean => {
    const errorItem = validateInvoiceUpload(invoice);
    console.log(errorItem);
    if (errorItem?.length > 0) {
      let errorsObject: any = {};
      errorItem.forEach((item: any) => {
        errorsObject[item.elementId] = item.message;
      });
      setValidationErrors({ ...errorsObject });
      return false;
    }
    return true;
  };

  const handleNextWithAPI = async () => {
    try {
      setIsCreatingInvoice(true);

      // Transform the current invoice data to match the API input format
      const billingAddress = getBillingAddress();
      const shippingAddress = getShippingAddress();

      // Format date to DD/MM/YYYY format as expected by the API
      const formatDateForAPI = (dateValue: any): string => {
        if (!dateValue) {
          const today = new Date();
          return `${today.getDate().toString().padStart(2, "0")}/${(
            today.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}/${today.getFullYear()}`;
        }

        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) {
            const today = new Date();
            return `${today.getDate().toString().padStart(2, "0")}/${(
              today.getMonth() + 1
            )
              .toString()
              .padStart(2, "0")}/${today.getFullYear()}`;
          }
          return `${date.getDate().toString().padStart(2, "0")}/${(
            date.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}/${date.getFullYear()}`;
        } catch (error) {
          const today = new Date();
          return `${today.getDate().toString().padStart(2, "0")}/${(
            today.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}/${today.getFullYear()}`;
        }
      };

      const requestData = {
        billingAddress: {
          name: billingAddress?.partyName || "",
          address1: billingAddress?.address1 || "",
          pincode: billingAddress?.postalCode || "",
          state: billingAddress?.state || "",
          city: billingAddress?.city || "",
        },
        shippingAddress: {
          name: shippingAddress?.partyName || "",
          address1: shippingAddress?.address1 || "",
          pincode: shippingAddress?.postalCode || "",
          state: shippingAddress?.state || "",
          city: shippingAddress?.city || "",
        },
        invoiceItems:
          safeInvoiceData.items?.map((item: any, index: number) => ({
            item: item.item || "",
            itemTypeId: item.itemTypeId || "",
            orderItemSeqId: item.orderItemSeqId || "",
            itemSeqId: index + 1,
            size: item.size || "",
            brandName: item.brandName || "",
            hsnCode: item.hsnCode || "",
            quantity: item.quantity?.toString() || "0",
            quantityUOM: item.quantityUOM || "",
            finalUnitPrice: item.pricePerUnit?.toString() || "0.00",
            gst: item.gst?.toString() || "0",
            amount: item.amount?.toString() || "0.00",
          })) || [],
        adjustments: adjustments,
        orderId: orderId,
      };

      if (!validateInputs(requestData)) {
        throw new Error(
          "One of more validations have failed. Please check input and try again"
        );
      }

      console.log("Creating external invoice with data:", requestData);

      // Call the createExternalInvoice API
      const result = await dispatch(
        generateInvoiceFromAccounts(requestData) as any
      );

      if (result.payload?.status === 200 || result.payload?.status === 201) {
        console.log("Invoice created successfully:", result.payload.data);
        setError({ open: false, message: "" });

        // Save content if invoice created successfully
        await store
        .dispatch(createOrderContent(contentDetails?.content))
        .unwrap()
        .then(async (res) => {
          if (res.status === 200) {
            setSuccess({
              open: true,
              message: "Sucessfully Saved Uploaded Invoice File.",
            });
            const reqBody = {
              orderId: contentDetails?.status?.orderId,
              status: contentDetails?.status?.statusId,
              invoiceStatus: "UPDATED",
            };
            if (contentDetails.delivered) {
              await store
                .dispatch(updateOrderStatus(reqBody))
                .unwrap()
                .then((res) => {
                  if (res.status === 200) {
                    setSuccess({
                      open: true,
                      message: "Updated Status Successfully",
                    });
                  } else {
                    setError({ open: true, message: "Update Status Failed" });
                  }
                })
                .catch(() => {
                  setError({ open: true, message: "Update Status Failed" });
                });
            }
          }
        })
        .catch((err: any) => {
          console.log(err);
          setError({
            open: true,
            message: "Could not Save Uploaded Invoice File",
          });
        });

        // Extract invoiceId from the response and add it to the invoice data
        const invoiceId = result.payload?.data?.invoice?.invoice?.invoiceId;
        if (invoiceId && onDataChange) {
          onDataChange("prism_invoice_id", invoiceId);
        }

        // Show success state
        setInvoiceCreated(true);
        // Notify parent component
        if (onInvoiceCreated) {
          onInvoiceCreated(true);
        }
      } else {
        console.error("Failed to create invoice:", result.payload?.error);
        setError({
          open: true,
          message:
            result.payload?.error ||
            "Failed to create invoice. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Error creating invoice:", error);
      setError({
        open: true,
        message:
          error.message || "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <Box
      className="invoice-verification-form"
      sx={{
        borderRadius: 1,
        backgroundColor: "white",
      }}
    >
      {invoiceCreated ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#4caf50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Check
              sx={{
                color: "white",
                fontSize: 40,
                fontWeight: "bold",
              }}
            />
          </Box>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            Invoice Created Successfully!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
            Your invoice has been created and saved successfully.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              onClick={() => {
                setInvoiceCreated(false);
                // Reset form data for new invoice
                setInvoiceHeadData({
                  invoiceName: "",
                  description: "",
                  customPONumber: "",
                  referenceDate: null,
                  expectedDeliveryDate: null,
                  fulfilledByPartyId: "",
                  paymentTerms: "",
                  termsNConditions: "",
                  adjustments: [],
                  hsnAck: false,
                  grandTotal: 0,
                  totalTaxAmount: 0,
                  totalAmount: 0,
                  invoiceContactMechs: [],
                  items: [],
                  totalAdjustedAmount: 0,
                  totalAmountWithoutAdjustment: 0,
                  externalRoundedOff: 0,
                  discount: {
                    amount: 0,
                    tax: 0
                  }
                });
                // Notify parent to reset seller selection and file upload state
                if (onInvoiceCreated) {
                  onInvoiceCreated(false);
                }
              }}
              sx={{
                borderRadius: 0,
                backgroundColor: "#22438C",
                color: "white",
                px: 4,
                py: 2,
                textTransform: "none",
                ":hover": {
                  backgroundColor: "#0060a1",
                },
                minWidth: "200px",
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "20px",
              }}
            >
              Create Another Invoice
            </Button>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 0,
                backgroundColor: "#f0f0f0",
                color: "#22438C",
                px: 4,
                py: 2,
                textTransform: "none",
                ":hover": {
                  backgroundColor: "#e4e4e4",
                },
                minWidth: "200px",
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "20px",
              }}
            >
              Go to Invoice List
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box className="invoice-input-table">
            <Stack px={6}>
              <Typography style={{ fontWeight: "bold" }}>
                Invoice details
              </Typography>
            </Stack>
          </Box>

          {/* Billing and Shipping Information */}
          <Box className="invoice-input-table">
            <Stack px={6} pt={2}>
              <Typography style={{ fontWeight: "bold" }}>
                Billing & Shipping Details
              </Typography>
            </Stack>
          </Box>
          <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
            <Grid container spacing={1} px={3}>
              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "#22438C", fontWeight: 600 }}
                >
                  Billing Address
                </Typography>
                <Grid container spacing={2}>
                  {/* <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Party Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Billing Party Name"
                      value={billingAddress?.partyName || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "billingAddress.partyName",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Address
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Billing Address"
                      value={billingAddress?.address1 || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "billingAddress.address1",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      multiline
                      rows={2}
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`billingAddress.address1`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`billingAddress.address1`]}
                    >
                      {validationErrors?.[`billingAddress.address1`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      City
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="City"
                      value={billingAddress?.city || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange("billingAddress.city", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`billingAddress.city`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`billingAddress.city`]}
                    >
                      {validationErrors?.[`billingAddress.city`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      State
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="State"
                      value={billingAddress?.state || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "billingAddress.state",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`billingAddress.state`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`billingAddress.state`]}
                    >
                      {validationErrors?.[`billingAddress.state`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Pincode
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Pincode"
                      value={billingAddress?.postalCode || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "billingAddress.postalCode",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`billingAddress.pincode`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`billingAddress.pincode`]}
                    >
                      {validationErrors?.[`billingAddress.pincode`]}
                    </FormHelperText>
                  </Grid>
                  {/* <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      GSTIN
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="GSTIN"
                      value={billingAddress?.gstin || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "billingAddress.gstin",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Grid> */}
                </Grid>
              </Grid>

              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "#22438C", fontWeight: 600 }}
                >
                  Shipping Address
                </Typography>
                <Grid container spacing={2}>
                  {/* <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Party Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Shipping Party Name"
                      value={shippingAddress?.partyName || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.partyName",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Address
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Shipping Address"
                      value={shippingAddress?.address1 || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.address1",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      multiline
                      rows={2}
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`shippingAddress.address1`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`shippingAddress.address1`]}
                    >
                      {validationErrors?.[`shippingAddress.address1`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      City
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="City"
                      value={shippingAddress?.city || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.city",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`shippingAddress.city`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`shippingAddress.city`]}
                    >
                      {validationErrors?.[`shippingAddress.city`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      State
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="State"
                      value={shippingAddress?.state || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.state",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`shippingAddress.state`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`shippingAddress.state`]}
                    >
                      {validationErrors?.[`shippingAddress.state`]}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      Pincode
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Pincode"
                      value={shippingAddress?.postalCode || ""}
                      disabled={true}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.postalCode",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                      error={!!validationErrors?.[`shippingAddress.pincode`]}
                    />
                    <FormHelperText
                      error={!!validationErrors?.[`shippingAddress.pincode`]}
                    >
                      {validationErrors?.[`shippingAddress.pincode`]}
                    </FormHelperText>
                  </Grid>
                  {/* <Grid item xs={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontSize: "0.9rem" }}
                    >
                      GSTIN
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="GSTIN"
                      value={shippingAddress?.gstin || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "shippingAddress.gstin",
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: "#f6f6f6",
                        "& input::placeholder": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Grid> */}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {/* Invoice Items Table */}
          <Box className="invoice-input-table" ref={tableRef}>
            <Stack px={6}>
              <Typography style={{ fontWeight: "bold" }}>
                Item details
              </Typography>
            </Stack>
            <TableContainer
              className="invoice-table"
              sx={{
                py: 2,
              }}
            >
              <Table sx={{ minWidth: 1300 }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="18%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS.ITEM_NAME
                          .label
                      }
                    </TableCell>
                    <TableCell width="12%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS.ITEM_HSN_CODE
                          .label
                      }
                    </TableCell>
                    <TableCell width="12%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS
                          .ITEM_BRAND_NAME.label
                      }
                    </TableCell>
                    <TableCell width="12%" className="tableCell">
                      Specification
                    </TableCell>
                    {/* <TableCell width="10%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS.ITEM_HSN_CODE
                          .label
                      }
                    </TableCell> */}
                    <TableCell width="8%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS
                          .ITEM_QUANTITY_UOM.label
                      }
                    </TableCell>
                    <TableCell width="8%" className="tableCell">
                      {
                        ORDER_WIZARD_ITEMS.ORDER_TABLE_HEAD_ITEMS.ITEM_QUANTITY
                          .label
                      }
                    </TableCell>
                    <TableCell width="10%" className="tableCell">
                      Unit Price
                    </TableCell>
                    <TableCell width="8%" className="tableCell">
                      GST (%)
                    </TableCell>
                    <TableCell width="10%" className="tableCell">
                      Amount
                    </TableCell>
                    <TableCell width="2%" className="tableCell">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceHeaderData?.items?.map((item: any, index: number) => {
                    if (item.status && item.status == ITEM_STATUS.REJECTED) {
                      return "";
                    } else {
                      return (
                        <TableRow
                          sx={{ alignContent: "flex-start" }}
                          key={index}
                        >
                          {/* Item Name */}
                          <TableCell width="18%" className="tableCell">
                            <TextField
                              className="item"
                              fullWidth
                              placeholder={
                                ORDER_INPUT_TABLE_ROW?.item?.placeholder
                              }
                              size="small"
                              value={invoiceHeaderData?.items[index]?.item}
                              disabled={true}
                              onChange={(event: any) => {
                                handleItemNameChange(event, index);
                              }}
                              onFocus={(event: any) => {
                                setActiveItemNameIndex(index);
                                setFilteredProducts([]);
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`item_${index}`]}
                            />
                            {invoiceHeaderData?.items[index]?.item &&
                              activeItemNameIndex === index && (
                                <Paper className="options-list">
                                  {filteredProducts?.length > 0 &&
                                    filteredProducts?.map(
                                      (product: any, ind: number) => (
                                        <MenuItem
                                          key={ind}
                                          onClick={() =>
                                            handleSelectItem(product, index)
                                          }
                                        >
                                          {product.productName +
                                            " | " +
                                            product.brandName}
                                        </MenuItem>
                                      )
                                    )}
                                </Paper>
                              )}
                            <FormHelperText
                              error={!!validationErrors?.[`item_${index}`]}
                            >
                              {validationErrors?.[`item_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Category */}
                          <TableCell width="12%" className="tableCell">
                            <TextField
                              fullWidth
                              size="small"
                              value={item?.hsnCode || ""}
                              disabled={true}
                              onChange={(event: any) => {
                                handleInputChange(
                                  index,
                                  "hsnCode",
                                  event?.target?.value,
                                  `hsnCode_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`hsnCode_${index}`]}
                            ></TextField>
                            <FormHelperText
                              error={!!validationErrors?.[`hsnCode_${index}`]}
                            >
                              {validationErrors?.[`hsnCode_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Brand Name */}
                          <TableCell width="12%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder={
                                ORDER_INPUT_TABLE_ROW?.brandName?.placeholder
                              }
                              size="small"
                              value={invoiceHeaderData?.items[index]?.brandName}
                              disabled={true}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleInputChange(
                                  index,
                                  "brandName",
                                  event?.target?.value,
                                  `brandName_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`brandName_${index}`]}
                            />
                            <FormHelperText
                              error={!!validationErrors?.[`brandName_${index}`]}
                            >
                              {validationErrors?.[`brandName_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Specification */}
                          <TableCell width="12%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder="Specification"
                              size="small"
                              value={item?.size}
                              disabled={true}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleInputChange(
                                  index,
                                  "size",
                                  event?.target?.value,
                                  `size_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`size_${index}`]}
                            />
                            <FormHelperText
                              error={!!validationErrors?.[`size_${index}`]}
                            >
                              {validationErrors?.[`size_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* HSN Code */}
                          {/* <TableCell width="10%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder={
                                ORDER_INPUT_TABLE_ROW.hsn?.placeholder
                              }
                              size="small"
                              value={invoiceHeaderData?.items[index]?.hsnCode}
                              onChange={(event: any) => {
                                handleInputChange(
                                  index,
                                  "hsnCode",
                                  event?.target?.value,
                                  `hsnCode_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`hsnCode_${index}`]}
                            />
                            <FormHelperText
                              error={!!validationErrors?.[`hsnCode_${index}`]}
                            >
                              {validationErrors?.[`hsnCode_${index}`]}
                            </FormHelperText>
                          </TableCell> */}

                          {/* UOM */}
                          <TableCell width="8%" className="tableCell">
                            <Select
                              fullWidth
                              size="small"
                              value={item?.quantityUOM || ""}
                              disabled={true}
                              onChange={(event: any) => {
                                handleInputChange(
                                  index,
                                  "quantityUOM",
                                  event?.target?.value,
                                  `quantityUOM_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={
                                !!validationErrors?.[`quantityUOM_${index}`]
                              }
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected) {
                                  return (
                                    <p className="selectPlaceholder">
                                      {
                                        ORDER_INPUT_TABLE_ROW?.quantityUOM
                                          ?.placeholder
                                      }
                                    </p>
                                  );
                                }
                                const selectedUOM = ITEM_UOMs?.find(
                                  (uom: any) => uom._id === selected
                                );
                                return selectedUOM?.name || selected;
                              }}
                            >
                              {ITEM_UOMs?.map((uomItem: any) => (
                                <MenuItem
                                  key={uomItem?._id}
                                  value={uomItem?._id}
                                >
                                  {uomItem?.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText
                              error={
                                !!validationErrors?.[`quantityUOM_${index}`]
                              }
                            >
                              {validationErrors?.[`quantityUOM_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Quantity */}
                          <TableCell width="8%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder={
                                ORDER_INPUT_TABLE_ROW?.quantity?.placeholder
                              }
                              size="small"
                              value={item?.quantity}
                              disabled={true}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleInputChange(
                                  index,
                                  "quantity",
                                  event?.target?.value,
                                  `quantity_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`quantity_${index}`]}
                            />
                            <FormHelperText
                              error={!!validationErrors?.[`quantity_${index}`]}
                            >
                              {validationErrors?.[`quantity_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Unit Price */}
                          <TableCell width="10%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder="Unit Price"
                              size="small"
                              value={item?.pricePerUnit || ""}
                              disabled={true}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleInputChange(
                                  index,
                                  "pricePerUnit",
                                  event?.target?.value,
                                  `pricePerUnit_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={
                                !!validationErrors?.[`pricePerUnit_${index}`]
                              }
                            />
                            <FormHelperText
                              error={
                                !!validationErrors?.[`pricePerUnit_${index}`]
                              }
                            >
                              {validationErrors?.[`pricePerUnit_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* GST % */}
                          <TableCell width="8%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder="GST %"
                              size="small"
                              value={item?.gst || ""}
                              disabled={true}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleInputChange(
                                  index,
                                  "gst",
                                  event?.target?.value,
                                  `gst_${index}`
                                );
                              }}
                              sx={{
                                backgroundColor: "#f6f6f6",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                              error={!!validationErrors?.[`gst_${index}`]}
                            />
                            <FormHelperText
                              error={!!validationErrors?.[`gst_${index}`]}
                            >
                              {validationErrors?.[`gst_${index}`]}
                            </FormHelperText>
                          </TableCell>

                          {/* Amount */}
                          <TableCell width="10%" className="tableCell">
                            <TextField
                              fullWidth
                              placeholder="Amount"
                              size="small"
                              value={
                                item?.amount
                                  ? formatAmount(parseFloat(item.amount))
                                  : ""
                              }
                              disabled={true}
                              sx={{
                                backgroundColor: "#e0e0e0",
                                "& input::placeholder": {
                                  fontSize: "0.8rem",
                                },
                              }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell width="2%" className="tableCell">
                            <Stack direction={"row"}>
                              <IconButton
                                sx={{ color: "#ED1B24" }}
                                onClick={() => handleDeleteItemRow(index)}
                              >
                                <DeleteCustom />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  })}
                </TableBody>
              </Table>
              <Box
                sx={{
                  mx: 4,
                  mt: 2,
                }}
              >
                <FormHelperText error={!!validationErrors?.itemsDiv}>
                  {validationErrors?.itemsDiv}
                </FormHelperText>
                {/* <Button
                  onClick={() => {
                    setOpenItemList(true);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: 0,
                  }}
                >
                  <AddBoxOutlined sx={{ color: "#22438C" }} />
                  <Typography color={"#22438C"}>Add more</Typography>
                </Button> */}
              </Box>

              <Box
                className="finalAmount-box"
                sx={{
                  mt: "15px",
                  p: 3,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
                  Invoice Summary
                </Typography>
                <Stack spacing={2}>
                  <Stack>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2">
                        {/* {formatAmount(invoiceHeaderData?.totalAmount || 0)} */}
                        ₹
                        {!Number.isNaN(Number(invoiceHeaderData?.totalAmount))
                          ? Number(invoiceHeaderData?.totalAmount ?? 0).toFixed(
                              2
                            )
                          : "0.00"}
                      </Typography>
                    </Box>
                    {adjustments.map((adjustment: any, index: number) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "right", mb: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "nowrap", mr: 22 }}
                        >
                          {adjustment?.description || `Adjustment ${index + 1}`}
                          :
                        </Typography>
                        <Typography variant="body2">
                          ₹
                          {!Number.isNaN(Number(adjustment?.adjustmentAmount))
                            ? Number(adjustment?.adjustmentAmount ?? 0).toFixed(
                                2
                              )
                            : "0.00"}
                        </Typography>
                      </Box>
                    ))}
                    {adjustments && adjustments?.length > 1 && (
                      <Box>
                        <Typography variant="body2">
                          Total Amount (Adjusted):
                        </Typography>
                        <Typography variant="body2">
                          ₹
                          {!Number.isNaN(
                            Number(adjustmentValues?.totalAdjustedAmount)
                          )
                            ? Number(
                                adjustmentValues?.totalAdjustedAmount ?? 0
                              ).toFixed(2)
                            : "0.00"}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <Stack>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {!isNaN(
                          Number(adjustmentValues?.totalAdjustedAmount)
                        ) &&
                          Number(adjustmentValues?.totalAdjustedAmount) > 0 &&
                          "Gross Total:"}
                      </Typography>
                      <Typography variant="body2">
                        {!isNaN(
                          Number(adjustmentValues?.totalAdjustedAmount)
                        ) &&
                          Number(adjustmentValues?.totalAdjustedAmount) > 0 &&
                          `₹${
                            !Number.isNaN(
                              Number(adjustmentValues?.totalAdjustedAmount)
                            )
                              ? (
                                  Number(invoiceHeaderData?.totalAmount) -
                                  Number(
                                    adjustmentValues?.totalAdjustedAmount ?? 0
                                  )
                                ).toFixed(2)
                              : "0.00"
                          }`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Total Tax:</Typography>
                      <Typography variant="body2">
                        {/* {formatAmount(invoiceHeaderData?.totalTaxAmount || 0)} */}
                        {adjustmentValues?.totalAdjustmentTax
                          ? `₹${
                              !Number.isNaN(
                                BigNumber(invoiceHeaderData?.totalTaxAmount)
                                  .minus(
                                    BigNumber(
                                      adjustmentValues?.totalAdjustmentTax ?? 0
                                    )
                                  )
                                  .toNumber()
                              )
                                ? BigNumber(invoiceHeaderData?.totalTaxAmount)
                                    .minus(
                                      BigNumber(
                                        adjustmentValues?.totalAdjustmentTax
                                      )
                                    )
                                    .toNumber()
                                    ?.toFixed(2)
                                : 0
                            }`
                          : `₹${
                              !Number.isNaN(
                                Number(invoiceHeaderData?.totalTaxAmount)
                              )
                                ? Number(
                                    invoiceHeaderData?.totalTaxAmount
                                  )?.toFixed(2)
                                : 0
                            }`}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pt: 1,
                        borderTop: "1px solid #ddd",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#22438C" }}
                      >
                        Grand Total:
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#22438C" }}
                      >
                        {/* {formatAmount(invoiceHeaderData?.grandTotal || 0)} */}
                        {invoiceHeaderData?.grandTotal
                          ? `₹${
                              !Number.isNaN(
                                BigNumber(invoiceHeaderData?.grandTotal)
                                  .minus(
                                    BigNumber(
                                      adjustmentValues?.grandAdjustedAmount ?? 0
                                    )
                                  )
                                  .toNumber()
                              )
                                ? BigNumber(invoiceHeaderData?.grandTotal)
                                    .minus(
                                      BigNumber(
                                        adjustmentValues?.grandAdjustedAmount
                                      )
                                    )
                                    .toNumber()
                                    ?.toFixed(2)
                                : 0
                            }`
                          : `₹${
                              !Number.isNaN(
                                Number(invoiceHeaderData?.grandTotal)
                              )
                                ? Number(
                                    invoiceHeaderData?.grandTotal
                                  )?.toFixed(2)
                                : 0
                            }`}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
                {/* <Button
                  onClick={() => setOpenDiscounts(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 0,
                  }}
                >
                  <AddBoxOutlined sx={{ color: "#22438C" }} />
                  <Typography color={"#22438C"}>Add Discounts</Typography>
                </Button> */}
              </Box>
              <Box
                sx={{
                  mx: 4,
                  mt: 2,
                }}
              >
                <FormHelperText error={!!validationErrors?.items_div}>
                  {validationErrors?.items_div}
                </FormHelperText>
              </Box>
            </TableContainer>

            <AdjustmentDialog
              open={openDiscounts}
              prevAdjustments={adjustments}
              onConfirm={addAdjustments}
              setOpen={setOpenDiscounts}
            />

            {/* Create Invoice Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 2,
                mt: 4,
                mb: 4,
                px: 6,
              }}
            >
              <Button
                onClick={handleNextWithAPI}
                disabled={isCreatingInvoice}
                sx={{
                  borderRadius: 0,
                  backgroundColor: "#22438C",
                  color: "white",
                  px: 6,
                  py: 2,
                  textTransform: "none",
                  ":hover": {
                    backgroundColor: "#0060a1",
                  },
                  minWidth: "220px",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "20px",
                }}
              >
                {isCreatingInvoice ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading Invoice...
                  </>
                ) : (
                  "Upload Invoice"
                )}
              </Button>
              <Button
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: 0,
                  backgroundColor: "#f0f0f0",
                  color: "#22438C",
                  px: 6,
                  py: 2,
                  textTransform: "none",
                  ":hover": {
                    backgroundColor: "#e4e4e4",
                  },
                  minWidth: "220px",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "20px",
                }}
              >
                Cancel
              </Button>
            </Box>
            <OrderItemListDialog
              open={openItemList}
              orderItems={orderItems}
              onConfirm={handleAddInvoiceRow}
              onClose={() => setOpenItemList(false)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default OrderInvoiceVerificationForm;
